import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENTS } from '../utils/analytics';
import { AuthContext } from '../context/AuthContext';
import config from '../config/config';
import PortfolioPieChart from './PortfolioPieChart';
import StockCard from './StockCard';
import PortfolioStockHeader from './PortfolioStockHeader';
import ChartToggle from './ChartToggle';
import PerformanceChart from './PerformanceChart';
import PortfolioGrowthChart from './PortfolioGrowthChart';

import '../styles/Watchlist.css';


const Portfolio = () => {
        const navigate = useNavigate();
        const [portfolio, setPortfolio] = useState([]);
        const [searchTerm, setSearchTerm] = useState('');
        const [searchResults, setSearchResults] = useState([]);
        const [selectedStock, setSelectedStock] = useState(null);
        const [quantity, setQuantity] = useState(1);
        const [isSearching, setIsSearching] = useState(false);
        const [showQuantityModal, setShowQuantityModal] = useState(false);
        const [activeOverviewTab, setActiveOverviewTab] = useState('metrics');
        const [sortBy, setSortBy] = useState('total_growth');
        const [sortOrder, setSortOrder] = useState('desc');
        const [filteredStocks, setFilteredStocks] = useState(null);
        const [activeChartView, setActiveChartView] = useState('value');
        const [activeDistributionView, setActiveDistributionView] = useState('stocks');
        const [performanceData, setPerformanceData] = useState([]);
        const [selectedRange, setSelectedRange] = useState('year');
        const [growthData, setGrowthData] = useState([]);
        const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
        const [preferredCurrency, setPreferredCurrency] = useState(null);
        const { token, getAuthHeaders } = useContext(AuthContext);
        const [availableCurrencies] = useState(['USD', 'EUR', 'GBP', 'JPY']); // Remove setter if not needed
        const CURRENCY_SYMBOLS = {
            'USD': '$',
            'EUR': '€',
            'GBP': '£',
            'JPY': '¥'
        };
            
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const [purchaseDate, setPurchaseDate] = useState(oneYearAgo.toISOString().split('T')[0]);
        const [performanceSummary, setPerformanceSummary] = useState({
            averageGrowth: 0,
            totalGrowth: 0,
            periodGrowth: 0,
            currency: preferredCurrency || 'EUR'
        });
        

        // Define fetchPortfolio first
        const fetchPortfolio = useCallback(async () => {
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(config.apiUrl + '/api/portfolio?currency=' + preferredCurrency, {
                    headers: headers
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                setPortfolio(data);
                setIsOverviewExpanded(true);
            } catch (err) {
                console.error('Portfolio fetch error:', err);
            }
        }, [getAuthHeaders, preferredCurrency]);
    
        // Define handleRangeChange
        const handleRangeChange = useCallback(async (range) => {
            setSelectedRange(range);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${config.apiUrl}/api/portfolio/performance?range=${range}&currency=${preferredCurrency}`, {
                    headers: headers
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Important: These need to be the correct data arrays
                setPerformanceData(data.totalValue || []);
                setGrowthData(data.growth || []);
                
                // Store summary data
                setPerformanceSummary(data.summary || {
                    averageGrowth: 0,
                    totalGrowth: 0,
                    periodGrowth: 0,
                    currency: preferredCurrency
                });
            } catch (error) {
                console.error('Performance data fetch error:', error);
            }
        }, [getAuthHeaders, preferredCurrency]);
    
        // Define handleCurrencyChange
        const handleCurrencyChange = useCallback(async (currency) => {
            try {
                // Track the currency change
                trackEvent(EVENTS.CURRENCY_CHANGE, { 
                    newCurrency: currency,
                    oldCurrency: preferredCurrency
                });

                // Update the UI immediately with the new currency
                setPreferredCurrency(currency);
                
                // Then update the backend
                const headers = await getAuthHeaders();
                const response = await fetch(config.apiUrl +'/api/portfolio/preferred-currency', {
                    method: 'POST',
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ currency })
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } catch (error) {
                console.error('Error updating preferred currency:', error);
            }
        }, [getAuthHeaders, preferredCurrency]);


    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined) return 'N/A';
        return Number(num).toLocaleString('de-DE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatCurrencyValue = (value, currency) => {
        if (value === null || value === undefined) return 'N/A';
        const symbol = CURRENCY_SYMBOLS[currency] || currency;
        return `${symbol}${formatNumber(value)}`;
    };

    const handleSearch = async (value) => {
        setSearchTerm(value);
        if (value.length < 2) {
            setSearchResults([]);
            return;
        }
    
        setIsSearching(true);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(config.apiUrl +'/api/stocks/search-by-name', {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: value })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            setSearchResults(data.data || []);
        } catch (error) {
            console.error('Search failed:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const fetchCompleteStockData = async (stockId) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(config.apiUrl + '/api/stocks/search', {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    name: searchResults.find(s => s.id === stockId)?.name,
                    sector: 'All',
                    currency: preferredCurrency
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            return data.data[0]; // Return the first matching stock
        } catch (error) {
            console.error('Failed to fetch complete stock data:', error);
            return null;
        }
    };

    
    const getPortfolioDistributionData = useCallback(() => {
        if (!portfolio || portfolio.length === 0) return [];
      
        // Create data for stock distribution
        if (activeDistributionView === 'stocks') {
          return portfolio.map(stock => ({
            name: stock.name,
            value: stock.total_value,
            id: stock.id
          }));
        }
        
        // Create data for sector distribution
        if (activeDistributionView === 'sectors') {
          // Group by sector and sum the values
          const sectorMap = portfolio.reduce((acc, stock) => {
            const sector = stock.sector || 'Unknown';
            if (!acc[sector]) {
              acc[sector] = 0;
            }
            acc[sector] += stock.total_value;
            return acc;
          }, {});
          
          // Convert to array format for the pie chart
          return Object.entries(sectorMap).map(([sector, value]) => ({
            name: sector,
            value: value
          }));
        }
        
        // Create data for country distribution
        if (activeDistributionView === 'countries') {
          // Group by country and sum the values
          const countryMap = portfolio.reduce((acc, stock) => {
            const country = stock.country || 'Unknown';
            if (!acc[country]) {
              acc[country] = 0;
            }
            acc[country] += stock.total_value;
            return acc;
          }, {});
          
          // Convert to array format for the pie chart
          return Object.entries(countryMap).map(([country, value]) => ({
            name: country,
            value: value
          }));
        }
        
        // Create data for currency distribution (as an example of another view)
        if (activeDistributionView === 'currencies') {
          // Group by trading currency and sum the values
          const currencyMap = portfolio.reduce((acc, stock) => {
            const currency = stock.trading_currency || 'Unknown';
            if (!acc[currency]) {
              acc[currency] = 0;
            }
            acc[currency] += stock.total_value;
            return acc;
          }, {});
          
          // Convert to array format for the pie chart
          return Object.entries(currencyMap).map(([currency, value]) => ({
            name: currency,
            value: value
          }));
        }

        // // Create data for market cap distribution
        // if (activeDistributionView === 'marketcap') {
        //     // Define market cap ranges
        //     const marketCapCategories = {
        //     'Large Cap (>$10B)': stock => stock.market_cap >= 10000000000,
        //     'Mid Cap ($2B-$10B)': stock => stock.market_cap >= 2000000000 && stock.market_cap < 10000000000,
        //     'Small Cap ($300M-$2B)': stock => stock.market_cap >= 300000000 && stock.market_cap < 2000000000,
        //     'Micro Cap (<$300M)': stock => stock.market_cap < 300000000 || !stock.market_cap
        //     };
            
        //     // Initialize the map
        //     const marketCapMap = {};
            
        //     // Group by market cap categories
        //     portfolio.forEach(stock => {
        //     for (const [category, condition] of Object.entries(marketCapCategories)) {
        //         if (condition(stock)) {
        //         if (!marketCapMap[category]) {
        //             marketCapMap[category] = 0;
        //         }
        //         marketCapMap[category] += stock.total_value;
        //         break;
        //         }
        //     }
        //     });
            
        //     // Convert to array format for the pie chart
        //     return Object.entries(marketCapMap).map(([category, value]) => ({
        //     name: category,
        //     value: value
        //     }));
        // }
        
        // Create data for dividend yield distribution
        if (activeDistributionView === 'dividend') {
            // Define dividend yield ranges
            const dividendCategories = {
            'Hohe Dividende (>4%)': stock => stock.dividend_yield > 4,
            'Medium Dividende (2-4%)': stock => stock.dividend_yield >= 2 && stock.dividend_yield <= 4,
            'Niedrige Dividende (0-2%)': stock => stock.dividend_yield > 0 && stock.dividend_yield < 2,
            'Keine Dividende': stock => !stock.dividend_yield || stock.dividend_yield === 0
            };
            
            // Initialize the map
            const dividendMap = {};
            
            // Group by dividend categories
            portfolio.forEach(stock => {
            for (const [category, condition] of Object.entries(dividendCategories)) {
                if (condition(stock)) {
                if (!dividendMap[category]) {
                    dividendMap[category] = 0;
                }
                dividendMap[category] += stock.total_value;
                break;
                }
            }
            });
            
            // Convert to array format for the pie chart
            return Object.entries(dividendMap).map(([category, value]) => ({
            name: category,
            value: value
            }));
        }
        
        // Create data for performance distribution
        if (activeDistributionView === 'performance') {
            // Define performance ranges
            const performanceCategories = {
            'Top Performer (>20%)': stock => stock.growth > 20,
            'Higherformer (5-20%)': stock => stock.growth >= 5 && stock.growth <= 20,
            'Neutral (-5% to 5%)': stock => stock.growth > -5 && stock.growth < 5,
            'Lowperformer (-20% to -5%)': stock => stock.growth >= -20 && stock.growth <= -5,
            'Schlechte Performer (<-20%)': stock => stock.growth < -20
            };
            
            // Initialize the map
            const performanceMap = {};
            
            // Group by performance categories
            portfolio.forEach(stock => {
            for (const [category, condition] of Object.entries(performanceCategories)) {
                if (condition(stock)) {
                if (!performanceMap[category]) {
                    performanceMap[category] = 0;
                }
                performanceMap[category] += stock.total_value;
                break;
                }
            }
            });
            
            // Convert to array format for the pie chart
            return Object.entries(performanceMap).map(([category, value]) => ({
            name: category,
            value: value
            }));
        }
        
        // Create data for P/E ratio distribution
        if (activeDistributionView === 'pe') {
            // Define P/E ratio ranges
            const peCategories = {
            'Niedriges KGV (<10)': stock => stock.trailing_p_e < 10 && stock.trailing_p_e > 0,
            'Mittleres KGV (10-20)': stock => stock.trailing_p_e >= 10 && stock.trailing_p_e <= 20,
            'Hohes KGV (20-50)': stock => stock.trailing_p_e > 20 && stock.trailing_p_e <= 50,
            'Sehr hohes KGV (>50)': stock => stock.trailing_p_e > 50,
            'Unklar': stock => !stock.trailing_p_e || stock.trailing_p_e <= 0
            };
            
            // Initialize the map
            const peMap = {};
            
            // Group by P/E categories
            portfolio.forEach(stock => {
            for (const [category, condition] of Object.entries(peCategories)) {
                if (condition(stock)) {
                if (!peMap[category]) {
                    peMap[category] = 0;
                }
                peMap[category] += stock.total_value;
                break;
                }
            }
            });
            
            // Convert to array format for the pie chart
            return Object.entries(peMap).map(([category, value]) => ({
            name: category,
            value: value
            }));
        }
        
        return [];
      }, [portfolio, activeDistributionView]);

        
    const handleStockSelect = (basicStockInfo) => {
        // Set initial stock info with basic data
        setSelectedStock({
            ...basicStockInfo,
            quantity: 1  // Set default quantity
        });
        setShowQuantityModal(true);
        setSearchResults([]);
        setSearchTerm('');
        
        // Fetch complete data in the background
        fetchCompleteStockData(basicStockInfo.id).then(completeData => {
            if (completeData) {
                setSelectedStock(prevStock => ({
                    ...completeData,
                    quantity: prevStock?.quantity || 1  // Use optional chaining and provide default
                }));
            }
        });
    };


    useEffect(() => {
        if (token && preferredCurrency) {  // Only fetch when both are available
            fetchPortfolio();
        }
    }, [token, preferredCurrency, fetchPortfolio]);

    useEffect(() => {
        if (!token) {
          navigate('/login');
        }
      }, [token, navigate]);
  
    useEffect(() => {
        const fetchPreferredCurrency = async () => {
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(config.apiUrl + '/api/portfolio/preferred-currency', {
                    headers
                });
                const data = await response.json();
                setPreferredCurrency(data.currency);
            } catch (error) {
                console.error('Error fetching preferred currency:', error);
            }
        };
        
        if (token) {
            fetchPreferredCurrency();
        }
    }, [token, getAuthHeaders]);

    useEffect(() => {
        if (token && preferredCurrency && selectedRange) {
            handleRangeChange(selectedRange);
        }
    }, [token, preferredCurrency, selectedRange, handleRangeChange]);
    

    // Replace the sortedPortfolio useMemo implementation in Portfolio.js
    const sortedPortfolio = useMemo(() => {
        // Start with either filtered stocks or the full portfolio
        const portfolioToSort = filteredStocks || portfolio;
        
        return [...portfolioToSort].sort((a, b) => {
            let aValue, bValue;
            
            // Map the sort keys to the actual property paths
            const propertyMap = {
                'total_growth': 'growth',
                'value': 'total_value',
                'next_year_growth': 'growth_estimates.next_year',
                'current_qtr_growth': 'growth_estimates.current_qtr',
                'next_qtr_growth': 'growth_estimates.next_qtr',
                'current_year_growth': 'growth_estimates.current_year'
            };
            
            // Get the actual property path to use for sorting
            const path = propertyMap[sortBy] || sortBy;
            
            // Helper function to get a value from an object with possible nested paths
            const getValue = (obj, propertyPath) => {
                const value = propertyPath.includes('.') ? 
                    propertyPath.split('.').reduce((curr, prop) => curr?.[prop], obj) : 
                    obj[propertyPath];
                
                // Convert to number and handle null/undefined
                return value === null || value === undefined ? -Infinity : Number(value);
            };
            
            aValue = getValue(a, path);
            bValue = getValue(b, path);
            
            // Ensure numeric comparison
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // Fallback to string comparison
            aValue = typeof aValue === 'string' ? aValue.toLowerCase() : String(aValue);
            bValue = typeof bValue === 'string' ? bValue.toLowerCase() : String(bValue);
            
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [portfolio, filteredStocks, sortBy, sortOrder]);
    

    // Add this function to handle adding stock to portfolio
    const handleAddToPortfolio = async () => {
        if (selectedStock && quantity > 0) {
            const bodyData = {
                quantity: Number(quantity),
                price: Number(selectedStock.current_price),
                purchase_date: purchaseDate,
                trackTransaction: true
            };
    
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(config.apiUrl +'/api/portfolio/' +selectedStock.id, {
                    method: 'POST',
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(bodyData)
                });
    
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Track the event
                trackEvent(EVENTS.ADD_TO_PORTFOLIO, {
                    stockId: selectedStock.id,
                    stockName: selectedStock.name,
                    quantity: quantity,
                    purchaseDate: purchaseDate
                });
    
                // Reset all states and close modal before fetching portfolio
                setShowQuantityModal(false);
                setSelectedStock(null);
                setQuantity(1);
                setPurchaseDate(oneYearAgo.toISOString().split('T')[0]);
                
                // Fetch portfolio after resetting states
                await fetchPortfolio();
            } catch (error) {
                console.error('Failed to add to portfolio:', error);
            }
        }
    };

    // Add this function to handle removal from portfolio
    const removeFromPortfolio = async (stockId) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(config.apiUrl + '/api/portfolio/' +stockId, {
                method: 'DELETE',
                headers: headers
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            await fetchPortfolio();
        } catch (error) {
            console.error('Failed to remove from portfolio:', error);
        }
    };

    const portfolioStats = useMemo(() => {
        const totalValue = portfolio.reduce((sum, stock) => sum + (stock.current_price * stock.quantity), 0);
        const totalCost = portfolio.reduce((sum, stock) => sum + (stock.average_buy_price * stock.quantity), 0);
        const totalGrowth = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
        const totalDividends = portfolio.reduce((sum, stock) => sum + ((stock.dividend || 0) * stock.quantity), 0);
        const numberOfStocks = portfolio.length;
        
        // Make sure we're getting numerical values
        const totalPurchasePrice = portfolio.reduce((sum, stock) => {
            const price = parseFloat(stock.average_buy_price) || 0;
            const quantity = parseFloat(stock.quantity) || 0;
            return sum + (price * quantity);
        }, 0);
        
        const averagePERatio = portfolio.length > 0 
            ? portfolio.reduce((sum, stock) => sum + (parseFloat(stock.trailing_p_e) || 0), 0) / portfolio.length 
            : 0;
        
        return {
            totalValue,
            totalCost,
            totalGrowth,
            totalDividends,
            totalDividendYield: totalValue > 0 ? (totalDividends / totalValue) * 100 : 0,
            numberOfStocks,
            totalPurchasePrice,
            averagePERatio,
            sectorDistribution: portfolio.reduce((acc, stock) => {
                acc[stock.sector] = (acc[stock.sector] || 0) + (stock.current_price * stock.quantity);
                return acc;
            }, {})
        };
    }, [portfolio]);

    return (
        <div className="portfolio-container">
            
            {/* Sektion 1: Portfolio Übersicht */}
            <section className="overview-section">
                <div className="section-card">
                <div className="portfolio-overview-header">
                    <h2 className='section-title'>Portfolio Übersicht</h2>
                    <div className="currency-selector">
                        <span className="currency-label">Währung</span>
                        <select 
                            value={preferredCurrency} 
                            onChange={(e) => handleCurrencyChange(e.target.value)}
                            className="modern-input currency-select"
                        >
                            {availableCurrencies.map(currency => (
                                <option key={currency} value={currency}>{currency}</option>
                            ))}
                        </select>
                    </div>
                </div>
                    
                    <div className={`portfolio-overview ${isOverviewExpanded ? 'block' : 'hidden'}`}>
                        {/* Add this tab navigation */}
                        <div className="chart-toggle mb-6 ">
                            <button 
                                className={`toggle-button ${activeOverviewTab === 'metrics' ? 'active' : ''}`}
                                onClick={() => setActiveOverviewTab('metrics')}
                            >
                                Kennzahlen
                            </button>
                            <button 
                                className={`toggle-button ${activeOverviewTab === 'distribution' ? 'active' : ''}`}
                                onClick={() => setActiveOverviewTab('distribution')}
                            >
                                Verteilung
                            </button>
                        </div>

                        {/* Conditional rendering based on active tab */}
                        {activeOverviewTab === 'metrics' ? (
                            <div className="metrics-grid">
                                <div className="metric">
                                <label>Gesamtwert</label>
                                <span>{formatCurrencyValue(portfolioStats.totalValue, preferredCurrency)}</span>
                                </div>
                                <div className="metric">
                                <label>Anzahl Aktien</label>
                                <span>{portfolioStats.numberOfStocks}</span>
                                </div>
                                <div className="metric">
                                <label>Summe Kaufpreise</label>
                                <span>{formatCurrencyValue(portfolioStats.totalPurchasePrice, preferredCurrency)}</span>
                                </div>
                                <div className="metric">
                                <label>Dividenden (Jahr)</label>
                                <span>{formatCurrencyValue(portfolioStats.totalDividends, preferredCurrency)}</span>
                                </div>
                                <div className="metric">
                                <label>Durchschn. Dividendenrendite</label>
                                <span>{formatNumber(portfolioStats.totalDividendYield)}%</span>
                                </div>
                                <div className="metric">
                                <label>Durchschn. KGV</label>
                                <span>{formatNumber(portfolioStats.averagePERatio)}</span>
                                </div>
                            </div>
                            ) : (
                            <div className="distribution-container">
                                {/* Distribution sub-tabs */}
                                <div className="filter-scroll-container">
                                    <div className="filter-cards">
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'stocks' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('stocks')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-coins"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Aktien</span>
                                        </div>
                                        </div>
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'sectors' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('sectors')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-industry"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Sektoren</span>
                                        </div>
                                        </div>
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'countries' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('countries')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-globe"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Länder</span>
                                        </div>
                                        </div>
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'currencies' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('currencies')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-money-bill-wave"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Währungen</span>
                                        </div>
                                        </div>

                                        {/* <div 
                                        className={`filter-card ${activeDistributionView === 'marketcap' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('marketcap')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-chart-line"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Marktkapitalisierung</span>
                                        </div>
                                        </div> */}
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'dividend' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('dividend')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-hand-holding-usd"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Dividenden</span>
                                        </div>
                                        </div>
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'performance' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('performance')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-chart-bar"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">Performance</span>
                                        </div>
                                        </div>
                                        
                                        <div 
                                        className={`filter-card ${activeDistributionView === 'pe' ? 'active' : ''}`}
                                        onClick={() => setActiveDistributionView('pe')}
                                        >
                                        <div className="filter-card-icon">
                                            <i className="fas fa-balance-scale"></i>
                                        </div>
                                        <div className="filter-card-content">
                                            <span className="filter-card-label">KGV</span>
                                        </div>
                                        </div>
                                    </div>
                                    </div>
                                
                                {/* Distribution chart */}
                                <div className="distribution-chart">
                                <PortfolioPieChart 
                                    data={getPortfolioDistributionData()}
                                    title={
                                    activeDistributionView === 'stocks' ? 'Aktien' : 
                                    activeDistributionView === 'sectors' ? 'Sektoren' : 
                                    activeDistributionView === 'currencies' ? 'Währungen' : 
                                    activeDistributionView === 'countries' ? 'Länder' :
                                    // activeDistributionView === 'marketcap' ? 'Marktkapitalisierung' :
                                    activeDistributionView === 'dividend' ? 'Dividenden' :
                                    activeDistributionView === 'performance' ? 'Performance' :
                                    activeDistributionView === 'pe' ? 'KGV' :
                                    'Verteilung'
                                    }
                                    currencySymbol={CURRENCY_SYMBOLS[preferredCurrency] || preferredCurrency}
                                />
                                </div>
                            </div>
                            )}
                    </div>
                </div>
            </section>


            {/* Sektion 2: Charts */}
            <section className="charts-section">
                <div className="section-card">
                    <h2 className='section-title'>Entwicklung</h2>
                    <ChartToggle 
                        activeView={activeChartView} 
                        onToggle={setActiveChartView} 
                    />
                    
                    <div className="timeframe-buttons">
                        <button 
                            className={`timeframe-button ${selectedRange === 'week' ? 'active' : ''}`}
                            onClick={() => handleRangeChange('week')}
                        >
                            1W
                        </button>
                        <button 
                            className={`timeframe-button ${selectedRange === 'month' ? 'active' : ''}`}
                            onClick={() => handleRangeChange('month')}
                        >
                            1M
                        </button>
                        <button 
                            className={`timeframe-button ${selectedRange === 'year' ? 'active' : ''}`}
                            onClick={() => handleRangeChange('year')}
                        >
                            1J
                        </button>
                    </div>
                    
                    {(() => {
                        // Use summary data from the backend
                        const summary = performanceSummary || {
                            averageGrowth: 0,
                            totalGrowth: 0,
                            periodGrowth: 0,
                            currency: preferredCurrency
                        };
                        
                        // Determine chart color based on view and values
                        const chartColor = (activeChartView === 'value' ? summary.periodGrowth : summary.averageGrowth) >= 0 
                            ? 'var(--success-color)' : 'var(--danger-color)';
                        
                        return (
                            <>
                            <div className="chart-value">
                                <div className={`total-value ${
                                    activeChartView === 'value' 
                                        ? (summary.periodGrowth >= 0 ? 'positive' : 'negative')
                                        : (summary.totalGrowth >= 0 ? 'positive' : 'negative')
                                }`}>
                                    {activeChartView === 'value' 
                                        ? formatCurrencyValue(portfolioStats.totalValue, preferredCurrency)
                                        : formatCurrencyValue(summary.totalGrowth, preferredCurrency)}
                                </div>
                                <div className={`growth-value ${summary.averageGrowth >= 0 ? 'positive' : 'negative'}`}>
                                    {summary.averageGrowth >= 0 ? '↑' : '↓'} 
                                    {Math.abs(activeChartView === 'value' ? summary.periodGrowth : summary.averageGrowth).toFixed(2)}
                                    {activeChartView === 'growth' ? 
                                        (selectedRange === 'year' ? ' Ø pro Monat' : ' Ø pro Tag') 
                                        : '%'}
                                </div>
                            </div>
                            
                            {activeChartView === 'value' ? (
                                <PerformanceChart 
                                    data={performanceData}
                                    dataKey="value"
                                    currency={preferredCurrency}
                                    chartColor={chartColor}
                                />
                            ) : (
                                <PortfolioGrowthChart 
                                    performanceData={growthData || []}
                                    currency={preferredCurrency}
                                    chartColor={chartColor}
                                    selectedRange={selectedRange}
                                />
                            )}
                            </>
                        );
                    })()}
                </div>
            </section>


            {/* Sektion 3: Aktienübersicht */}
            <section className="stocks-section">
                <div className="section-card">
                <PortfolioStockHeader
                    searchTerm={searchTerm}
                    onSearch={handleSearch}
                    sortBy={sortBy}
                    onSortChange={(value) => setSortBy(value)}
                    sortOrder={sortOrder}
                    onSortOrderChange={setSortOrder}
                    isSearching={isSearching}
                    searchResults={searchResults}
                    onStockSelect={handleStockSelect}
                    portfolio={portfolio}
                    onFilterChange={setFilteredStocks}
                />
                    
                    <div className="stock-grid">
                        {sortedPortfolio.length > 0 ? (
                            sortedPortfolio.map(stock => (
                                <StockCard
                                    key={stock.id}
                                    stock={stock}
                                    showQuantity={true}
                                    isPortfolio={true}
                                    onAction={removeFromPortfolio}
                                    actionIcon="fa-trash"
                                    actionType="remove"
                                    sortBy={sortBy}
                                    currency={preferredCurrency}
                                />
                            ))
                        ) : (
                            <div className="no-stocks-message">
                                <i className="fas fa-info-circle"></i>
                                <p>Keine Aktien gefunden, die den ausgewählten Kriterien entsprechen.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
    
            {/* Add Stock Modal */}
            {showQuantityModal && selectedStock && (
                <div className="modal-overlay">
                    <div className="modern-modal">
                        <h3>{selectedStock.name}</h3>
                        <p className="sector-info">{selectedStock.sector}</p>
                        
                        <div className="quantity-input">
                            <div className="label-with-info">
                                <label>Aktuelle Anzahl:</label>
                                <div className="tooltip">
                                    <i className="fas fa-question-circle info-icon"></i>
                                    <span className="tooltip-text">Die Anzahl der aktuell im Depot vorhanden Aktien.</span>
                                </div>
                            </div>
                            <input
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="modern-input"
                                placeholder="z.B. 100"
                            />
                        </div>
    
                        <div className="quantity-input">
                            <div className="label-with-info">
                                <label>Kaufdatum:</label>
                                <div className="tooltip">
                                    <i className="fas fa-question-circle info-icon"></i>
                                    <span className="tooltip-text">Die meisten Analysen beziehen sich auf das letzte Jahr. Für ältere Positionen reicht 'Vor mehr als einem Jahr'.</span>
                                </div>
                            </div>
                            <div className="date-input-container">
                                {purchaseDate === oneYearAgo.toISOString().split('T')[0] ? (
                                    <>
                                        <div 
                                            className="date-input-wrapper"
                                            onClick={() => {
                                                setPurchaseDate(new Date().toISOString().split('T')[0]);  // Set to today's date when switching to date picker
                                            }}
                                        >
                                            <input
                                                type="text"
                                                value="Vor mehr als einem Jahr"
                                                className="modern-input date-text"
                                                readOnly
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <button 
                                                className="date-toggle-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();  // Prevent double triggering
                                                    setPurchaseDate(new Date().toISOString().split('T')[0]);
                                                }}
                                                title="Genaues Datum auswählen"
                                            >
                                                <i className="fas fa-calendar-alt"></i>
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="date"
                                            value={purchaseDate}
                                            onChange={(e) => setPurchaseDate(e.target.value)}
                                            className="modern-input"
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        <button 
                                            className="date-toggle-btn"
                                            onClick={() => setPurchaseDate(oneYearAgo.toISOString().split('T')[0])}
                                            title="Vor mehr als einem Jahr"
                                        >
                                            <i className="fas fa-history"></i>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
    
                        <div className="modal-actions">
                            <button 
                                onClick={handleAddToPortfolio}
                                className="action-button primary"
                            >
                                <i className="fas fa-plus"></i> Zum Portfolio hinzufügen
                            </button>
                            <button 
                                onClick={() => setShowQuantityModal(false)}
                                className="action-button secondary"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Portfolio;